import {
	EnvironmentService,
	LinkAuthExceptionFactory
} from "phoenix/services";
import { AuthService } from './authService';
import SettingsService from "phoenix/services/settingsService";
import { IStorageAccessor } from "util/domain/storage-types";
import { CommonUtilsService } from "util/services";
import {
	IdpClientConfig,
	IdpAuth,
	IdpStorageKeys
} from "phoenix/domain";

export class WebAuthService extends AuthService {
	private readonly webPath = this.pxEnvironmentService.baseUrl;

	public readonly idpSettings:IdpClientConfig = {
		clientId: this.settingsService.idp.web.clientId,
		path: this.settingsService.idp.web.path,
		postLogoutUri: `${ this.webPath }#/login`,
		redirectUri: this.webPath,
		responseType: 'id_token token',			
		scope: this.settingsService.idp.web.scope
	};

	public constructor(
		$window:ng.IWindowService,
		$http: ng.IHttpService,
		$q: ng.IQService,
		$injector,
		$location,
		private utils:CommonUtilsService,
		settingsService:SettingsService,
		localStorageService:IStorageAccessor,
		sessionStorageService:IStorageAccessor,
		pxEnvironmentService:EnvironmentService,
		pxLinkAuthExceptionFactory:LinkAuthExceptionFactory
	) {
		super(
			$window,
			$http,
			$q,
			$injector,
			$location,
			settingsService,
			localStorageService,
			sessionStorageService,
			pxEnvironmentService,
			pxLinkAuthExceptionFactory
		);
	}

	public handle401() {
		if (this.$location.path() !== '/login') {
			this.redirectPath = this.$location.path();
		}

		return this.invalidateLogin()
			.then(url => {
				if(url){
					this.$window.location.assign(url);
				} 
			})
			.catch(() => {
				this.$location.path('/login');
			});
	}

	/**
	 * @ngdoc method
	 * @name pxAuthService#invalidateLogin
	 * @methodOf phoenix.pxAuthService
	 * @kind function
	 *
	 * @description
	 * Clears the users Phoenix token
	 *
	 */
	public invalidateLogin(inMemoryOnly:boolean = false) {
		let p = this.$q.resolve('');
		let idToken =  this.idToken || (this.authData ? this.authData.idToken : '');
		this.authData = this.userData = this.userStats = this.authToken = this.idToken = null;
		
		if (!inMemoryOnly) {
			Object.keys(IdpStorageKeys).forEach(identifier => {
				let key = IdpStorageKeys[identifier];
				this.localStorageService.removeStoredValue(key);
			});                    
		}

		if(idToken){
			p = this.getIdpConfig()
				.then(idpConfig =>{
					let logoutUri = `${idpConfig.end_session_endpoint}?`+
						`post_logout_redirect_uri=${encodeURIComponent(this.idpSettings.postLogoutUri)}&id_token_hint=${encodeURIComponent(idToken)}`;

					return logoutUri;					
				});
		}

		return p;
	}

	/**
	 * @ngdoc method
	 * @name pxAuthService#sendToIdp
	 * @methodOf phoenix.pxAuthService
	 * @kind function
	 *
	 * @description
	 * Redirects the client to the authorization URI discovered from the IDP configuration.
	 * Upon successful authorization, the client is redirected back to the configured callback URI.
	 *
	 */
	public sendToIdp(emailAddress:string, rememberMe:boolean = true) {
		let urlKeyValues = {
			'acr_values': `email:${emailAddress}`,
			'client_id': this.idpSettings.clientId,
			'redirect_uri': this.idpSettings.redirectUri,
			'response_type': 'token id_token',
			'scope': this.idpSettings.scope.join(' '),
			'nonce': this.utils.generateUid(),
			'state': this.utils.generateUid()
		};

		if (rememberMe) {
			this.rememberedEmail = emailAddress;
		}

		return this.getIdpConfig()
			.then(idpInfo => {	
				let url = idpInfo.authorization_endpoint + '?';

				url += Object.keys(urlKeyValues)
					.reduce((querystring, key, i, keys) => {
						let queryParam = `${key}=${encodeURIComponent(urlKeyValues[key])}${i < keys.length - 1 ? '&' : ''}`;
						return querystring += queryParam; 
					}, '');

				this.storeIdpState(urlKeyValues.state);
				this.$window.location.assign(url);
			});
	}

	private storeIdpState(state) {
		this.localStorageService.setStoredValue(IdpStorageKeys.IDP_STATE, state.toString());
	}
	
	/**
	 * @ngdoc method
	 * @name pxAuthService#validate
	 * @methodOf phoenix.pxAuthService
	 * @kind function
	 *
	 * @description
	 * Validates the authorization parameters supplied by the IDP and, if specified,
	 * persists them locally. 
	 *
	 */
	public validate(authData:IdpAuth){
		let p:ng.IPromise<void>;
		
		if(authData.error){
			p = this.$q.reject(new Error(authData.error));
		}
		else if(this.verifyIdpState(authData.state)) {
			this.storeToken(authData.accessToken, 'Bearer');
			this.authData = authData;

			p = this.hydrateUserData();
		}
		else {
			p = this.$q.reject(new Error('Invalid IDP state.'));
		}

		return p;
	}

	private verifyIdpState(state:number) {
		if(typeof(state) !== 'number') {
			throw new Error('Must provide a valid state value.');
		}

		let currState = parseInt(this.localStorageService.getStoredValue(IdpStorageKeys.IDP_STATE));

		return this.verifiedToken = (currState === state);
	}
}


// WEBPACK FOOTER //
// ./src/phoenix/services/auth/web-auth-service.ts